import { useEffect, useState } from "react";
import "./header.scss";

import {
  FaPhoneAlt,
  FaEnvelope,
  FaTwitter,
  FaInstagram,
  FaLinkedinIn,
  FaFacebookF,
  FaCalendarAlt,
  FaBars,
  FaTimes,
  FaChevronDown,
} from "react-icons/fa";

import { Link, NavLink, useLocation } from "react-router-dom";
import logo from "../images/header-image/logo.png";

import AppointmentModal from "./AppointmentModal";

const serviceGroups = [
  {
    title: "Fertility Treatments",
    links: [
      { to: "/ivf-treatment-in-delhi", label: "IVF Treatment" },
      { to: "/icsi", label: "ICSI" },
      { to: "/imsi", label: "IMSI" },
      { to: "/artificial-insemination-iui", label: "Artificial Insemination (IUI)" },
    ],
  },
  {
    title: "Advanced Lab Care",
    links: [
      { to: "/blastocyst-transfer", label: "Blastocyst Transfer" },
      { to: "/assisted-hatching", label: "Assisted Hatching" },
      { to: "/pre-genetic-screening-and-diagnosis", label: "PGT Screening" },
      { to: "/oocyte-and-embryo-freezing", label: "Oocyte & Embryo Freezing" },
    ],
  },
  {
    title: "Donor & Surrogacy",
    links: [
      { to: "/donor-egg-ivf", label: "Donor Egg IVF" },
      { to: "/donor-sperm-ivf", label: "Donor Sperm IVF" },
      { to: "/surrogacy", label: "Surrogacy" },
    ],
  },
  {
    title: "Diagnosis & Surgery",
    links: [
      { to: "/infertility-treatment-in-delhi", label: "Infertility Treatment" },
      { to: "/fertility-enhancing-laparoscopy-and-hysteroscopy", label: "Laparoscopy & Hysteroscopy" },
      { to: "/testicular-aspiration-or-tesa", label: "TESA / PESA" },
      { to: "/microtesa", label: "Micro TESA" },
    ],
  },
];

const navLinks = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About Dr. Ila Gupta" },
  { type: "services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/media", label: "Media" },
  { to: "/blog", label: "Blogs" },
  { to: "/videos", label: "Videos" },
  { to: "/contact", label: "Contact" },
];

const socialLinks = [
  { href: "https://twitter.com/dr_ila_gupta", label: "Twitter", icon: <FaTwitter /> },
  { href: "https://www.instagram.com/Ilaguptaivf", label: "Instagram", icon: <FaInstagram /> },
  { href: "https://www.linkedin.com/company/ilaguptaivf", label: "LinkedIn", icon: <FaLinkedinIn /> },
  { href: "https://www.facebook.com/ivfallindia", label: "Facebook", icon: <FaFacebookF /> },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [appointmentOpen, setAppointmentOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const servicesActive = serviceGroups.some((group) =>
    group.links.some((link) => location.pathname === link.to)
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const openAppointment = () => {
    setMobileOpen(false);
    setAppointmentOpen(true);
  };

  return (
    <>
      <div className="top-bar">
        <div className="top-bar__inner">
          <div className="top-left">
            <a href="tel:+919205020856">
              <FaPhoneAlt /> <span className="phone-number">+91 92050 20856</span>
            </a>
            <a href="mailto:ilagupta.ivf@gmail.com" className="top-email">
              <FaEnvelope /> <span>ilagupta.ivf@gmail.com</span>
            </a>
          </div>

          <div className="top-right">
            <div className="social-icons">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                >
                  {social.icon}
                </a>
              ))}
            </div>

            <button type="button" className="top-appointment-btn" onClick={openAppointment}>
              <FaCalendarAlt /> <span>Book Appointment</span>
            </button>
          </div>
        </div>
      </div>

      <header className={`main-header${scrolled ? " is-scrolled" : ""}`}>
        <div className="main-header__inner">
          <div className="logo">
            <Link to="/">
              <img src={logo} alt="Dr. Ila Gupta IVF" />
            </Link>
          </div>

          <nav className="nav-menu" aria-label="Main navigation">
            {navLinks.map((item) =>
              item.type === "services" ? (
                <div
                  key="services"
                  className={`nav-item has-mega${servicesActive ? " active" : ""}`}
                >
                  <span tabIndex={0}>
                    Services <FaChevronDown />
                  </span>

                  <div className="mega-dropdown">
                    <div className="mega-grid">
                      {serviceGroups.map((group) => (
                        <div className="mega-col" key={group.title}>
                          <h4>{group.title}</h4>
                          {group.links.map((link) => (
                            <Link key={link.to} to={link.to}>
                              {link.label}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink key={item.to} to={item.to} end={item.end}>
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <button
            type="button"
            className="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </header>

      <div
        className={`mobile-menu-overlay${mobileOpen ? " open" : ""}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden={!mobileOpen}
      >
        <nav
          className="mobile-menu"
          onClick={(e) => e.stopPropagation()}
          aria-label="Mobile navigation"
        >
          <div className="mobile-menu__head">
            <img src={logo} alt="Dr. Ila Gupta IVF" />
            <button
              type="button"
              className="mobile-close"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              <FaTimes />
            </button>
          </div>

          <div className="mobile-menu__links">
            {navLinks.map((item) =>
              item.type === "services" ? (
                <div className="mobile-accordion" key="services">
                  <button
                    type="button"
                    className="accordion-header"
                    onClick={() => setServicesOpen(!servicesOpen)}
                    aria-expanded={servicesOpen}
                  >
                    <span>Services</span>
                    <FaChevronDown className={servicesOpen ? "rotate" : ""} />
                  </button>

                  {servicesOpen && (
                    <div className="accordion-body">
                      {serviceGroups.map((group) => (
                        <div className="accordion-group" key={group.title}>
                          <p>{group.title}</p>
                          {group.links.map((link) => (
                            <Link key={link.to} to={link.to}>
                              {link.label}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink key={item.to} to={item.to} end={item.end}>
                  {item.label}
                </NavLink>
              )
            )}
          </div>

          <div className="mobile-menu__footer">
            <button type="button" className="appointment-btn" onClick={openAppointment}>
              <FaCalendarAlt /> Book Appointment
            </button>
            <a href="tel:+919205020856" className="mobile-call">
              <FaPhoneAlt /> +91 92050 20856
            </a>
          </div>
        </nav>
      </div>

      <AppointmentModal
        appointmentOpen={appointmentOpen}
        setAppointmentOpen={setAppointmentOpen}
      />
    </>
  );
};

export default Header;
